import type { AnswerRecord, Session } from "./types";

// Every PPS row (percent level) in the PPSv2 reference table, highest first.
const PPS_ROWS = [100, 90, 80, 70, 60, 50, 40, 30, 20, 10];

// The PPS columns in leftward-priority order: the leftmost is the stronger determinant.
const PPS_COLUMNS = [
  "q_pps_ambulation",
  "q_pps_activity",
  "q_pps_selfcare",
  "q_pps_intake",
  "q_pps_conscious",
];

// For each column, the PPS rows a given walkthrough answer is consistent with in the
// PPSv2 reference table (Appendix 2). This is a clinical interpretation of that table
// and is pending Big Bend Hospice sign-off in the next review round.
export const PPS_ROW_MAP: Record<string, Record<string, number[]>> = {
  q_pps_ambulation: {
    full: [100, 90, 80],
    reduced: [70, 60],
    mainly_sit_lie: [50],
    mainly_bed: [40],
    bed_bound: [30, 20, 10],
  },
  q_pps_activity: {
    normal: [100],
    some_evidence: [90, 80],
    significant_disease: [70, 60],
    unable: [50, 40],
    bed_bound: [30, 20, 10],
  },
  q_pps_selfcare: {
    full_care: [100, 90, 80, 70],
    occasional_assist: [60],
    considerable_assist: [50],
    mainly_assist: [40],
    total_care: [30, 20, 10],
  },
  q_pps_intake: {
    normal: [100, 90, 80, 70, 60, 50, 40],
    reduced: [80, 70, 60, 50, 40, 30],
    minimal: [20],
    mouth_care: [10],
  },
  q_pps_conscious: {
    full: [100, 90, 80, 70, 60, 50, 40, 30, 20],
    confusion: [60, 50, 40, 30, 20],
    drowsy: [50, 40, 30, 20, 10],
    coma: [10],
  },
};

// Derive the PPS from the plain-language walkthrough by leftward-priority best-fit:
// read the columns left to right, each narrowing the candidate rows, but skip a
// rightward column whose answer conflicts with the rows already fixed by the stronger
// leftward columns (it must not raise the score). The result is the best-fit row that
// survives. Averaging was replaced here because it can yield a value matching no real
// PPS row and cross an eligibility threshold the true row would not.
export function computePps(history: AnswerRecord[]): number {
  // Node ids may be bare (q_pps_ambulation) or module-prefixed (pps_scale__q_pps_ambulation),
  // so match a PPS answer to its column by the column suffix.
  const answers = new Map<string, string>();
  for (const r of history) {
    if (r.question.category !== "pps") continue;
    const column = PPS_COLUMNS.find((c) => r.nodeId === c || r.nodeId.endsWith(`__${c}`));
    if (column) answers.set(column, String(r.answer));
  }
  if (answers.size === 0) return 100;

  let candidates = [...PPS_ROWS];
  for (const column of PPS_COLUMNS) {
    const value = answers.get(column);
    if (value === undefined) continue;
    const rows = PPS_ROW_MAP[column]?.[value];
    if (!rows) continue;
    const narrowed = candidates.filter((row) => rows.includes(row));
    if (narrowed.length > 0) candidates = narrowed;
  }
  return Math.max(...candidates);
}

// FAST stage 7a (speech limited to a few words). Appendix 3 scores the "highest
// consecutive level of disability", so every lower item must be present to climb past
// it. Above 7a the sub-items are taken as the highest present, so a patient who speaks
// a few words and cannot walk reaches 7c without first losing all speech (7b).
const FAST_STAGE_7A = 7.1;

// FAST sub-stages are encoded as tenths (6.1 = 6a ... 7.4 = 7d); show them the way
// Appendix 3 writes them.
export function formatFastStage(value: number): string {
  const stage = Math.floor(value);
  const sub = Math.round((value - stage) * 10);
  return sub === 0 ? String(stage) : `${stage}${String.fromCharCode(96 + sub)}`;
}

export function computeFast(history: AnswerRecord[]): number {
  const items = history
    .filter((r) => r.question.category === "fast")
    .map((r) => {
      const options = r.question.options ?? [];
      const impairedScores = options.map((o) => o.score).filter((s): s is number => s !== undefined && s > 3);
      return {
        level: impairedScores.length > 0 ? Math.min(...impairedScores) : undefined,
        score: options.find((o) => o.value === String(r.answer))?.score,
      };
    })
    .sort((a, b) => (a.level ?? Infinity) - (b.level ?? Infinity));

  let stage = 3; // FAST baseline when nothing indicates a higher stage
  for (const item of items) {
    if (item.score !== undefined && item.score > 3) stage = Math.max(stage, item.score);
    else if (item.level !== undefined && item.level <= FAST_STAGE_7A) break;
  }
  return stage;
}

const STRATEGIES: Record<string, (history: AnswerRecord[]) => number> = {
  "pps-leftward": computePps,
  "fast-staging": computeFast,
};

export function setComputedValue(session: Session, name: string, value: number): Session {
  return { ...session, computed: { ...session.computed, [name]: value } };
}

export function getComputedValue(session: Session, name: string): number {
  const override = session.computed?.[name];
  if (override !== undefined) return override;
  const def = session.rules.computedValues?.[name];
  if (!def) throw new Error(`Unknown computed value "${name}"`);
  const strategy = STRATEGIES[def.strategy];
  if (!strategy) throw new Error(`Unknown computed strategy "${def.strategy}"`);
  return strategy(session.history);
}
